import { useRef, useState } from "react";
import { toast } from "sonner";
import { Play, Video, Trash2, Loader2 } from "lucide-react";
import { uploadVideoToCloudinary, VIDEO_LIMITS } from "@/lib/uploads";
import VideoLightbox from "@/components/VideoLightbox";

/**
 * Reusable "upload + manage" UI for a list of short video clips.
 *
 * Props:
 *   videos   — array of VideoRef objects
 *   onChange — called with the new array on add/remove
 *   testid   — root data-testid prefix (default: "video-field")
 */
export default function VideoUploadField({ videos = [], onChange, testid = "video-field" }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lightbox, setLightbox] = useState(null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file) return;
    setUploading(true);
    setProgress(0);
    try {
      const v = await uploadVideoToCloudinary(file, { onProgress: setProgress });
      onChange([...(videos || []), v]);
      toast.success("Video uploaded");
    } catch (err) {
      toast.error(err.message || "Upload failed");
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const remove = (idx) => {
    const next = [...videos];
    next.splice(idx, 1);
    onChange(next);
  };

  const maxMb = (VIDEO_LIMITS.maxBytes / 1024 / 1024).toFixed(0);
  const maxS = VIDEO_LIMITS.maxDurationSeconds;

  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium border border-stone-300 hover:border-amber-400 hover:bg-amber-50 transition-colors disabled:opacity-50"
          data-testid={`${testid}-pick`}
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Video className="w-3.5 h-3.5" />}
          {uploading ? `Uploading ${progress}%` : "Add video"}
        </button>
        <span className="text-[11px] text-stone-500">Up to {maxS}s · {maxMb} MB</span>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={onPick}
        data-testid={`${testid}-input`}
      />

      {videos.length > 0 && (
        <ul className="flex flex-wrap gap-3" data-testid={`${testid}-list`}>
          {videos.map((v, i) => (
            <li key={`${v.public_id}-${i}`} className="relative group" data-testid={`${testid}-item-${i}`}>
              <button
                type="button"
                onClick={() => setLightbox(v)}
                className="block w-32 h-20 rounded-lg overflow-hidden border border-stone-200 bg-stone-100 relative"
                data-testid={`${testid}-thumb-${i}`}
              >
                {v.poster_url ? (
                  <img src={v.poster_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-stone-400 text-xs">video</div>
                )}
                <span className="absolute inset-0 grid place-items-center bg-black/30 group-hover:bg-black/40 transition-colors">
                  <Play className="w-6 h-6 text-white fill-white drop-shadow" />
                </span>
                {v.duration > 0 && (
                  <span className="absolute bottom-1 right-1 text-[10px] font-medium text-white bg-black/60 px-1.5 py-0.5 rounded">
                    {Math.round(v.duration)}s
                  </span>
                )}
              </button>
              <button
                type="button"
                onClick={() => remove(i)}
                className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-white border border-stone-300 text-stone-500 hover:text-red-600 hover:border-red-300 grid place-items-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Remove video"
                data-testid={`${testid}-remove-${i}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {lightbox && <VideoLightbox video={lightbox} onClose={() => setLightbox(null)} />}
    </div>
  );
}
